// DRPX Courier Logo — unified coordinates everywhere
// DRP=38px, X=54px, pin centered on X, arrow underline
// Shared coords:
// DRP: x=20 y=38, fontSize=38
// X: x=176 y=44, fontSize=54, center ~198
// Pin: x=198 y=-8..21
// Underline arrow: 22 -> 222, head at 228
// Courier: x=242 y=38

interface DrpxLogoProps {
  /** Skalierungsfaktor (Default: `1` = 400×76 px). */
  size?: number;
  /** `true` = heller Text auf dunklem Hintergrund (Default: `true`). */
  dark?: boolean;
  /**
   * Reserviert: steuert das "Courier"-Suffix. Aktuell wird das Suffix
   * immer gerendert, unabhängig vom Prop-Wert. Mehrere Caller übergeben
   * `showCourier={true}`; der Prop bleibt aus Backward-Compat-Gründen hier.
   */
  showCourier?: boolean;
}

/** DRPX Courier — statische Variante (für Auth-Screens, Footer, Druck). */
export function DrpxLogo({ size, dark }: DrpxLogoProps) {
  const s = size || 1;
  const isDark = dark !== undefined ? dark : true;
  const textColor = isDark ? '#fff' : '#0c1a0e';
  return (
    <svg
      viewBox="0 -16 400 76"
      style={{ width: 400 * s, height: 76 * s, display: 'block' }}>
      <text
        x="20"
        y="40"
        fill={textColor}
        fontFamily="'Syne',sans-serif"
        fontSize="38"
        fontWeight="800"
        letterSpacing="2">
        DRP
      </text>
      <text
        x="176"
        y="44"
        fill="#f59e0b"
        fontFamily="'Syne',sans-serif"
        fontSize="54"
        fontWeight="800">
        X
      </text>
      <path
        d="M198 -10 Q212 -10 212 4 Q212 14 198 24 Q184 14 184 4 Q184 -10 198 -10 Z"
        fill="#4ade80" />
      <circle cx="198" cy="4" r="5" fill="#fff" />
      <line
        x1="22"
        y1="54"
        x2="222"
        y2="54"
        stroke="#16a34a"
        strokeWidth="2"
        strokeLinecap="round" />
      <path
        d="M218 48 L228 54 L218 60"
        stroke="#16a34a"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none" />
      <text
        x="242"
        y="40"
        fill={isDark ? 'rgba(255,255,255,.5)' : 'rgba(12,26,14,.4)'}
        fontFamily="'Syne',sans-serif"
        fontSize="22"
        fontWeight="700"
        letterSpacing="1">
        Courier
      </text>
    </svg>
  );
}

/** DRPX Courier — animierte Topbar-Variante (Glow, Bounce, Arrow-Travel). */
export function DrpxLogoTopbar({ size, dark }: DrpxLogoProps) {
  const s = size || 1;
  const isDark = dark !== undefined ? dark : true;
  const textColor = isDark ? '#fff' : '#0c1a0e';
  return (
    <svg
      viewBox="0 -16 400 76"
      style={{ width: 400 * s, height: 76 * s, display: 'block' }}>
      <defs>
        <filter id="xGlow">
          <feGaussianBlur stdDeviation="2.5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <clipPath id="arrowClip">
          <rect x="22" y="50" width="210" height="10" />
        </clipPath>
      </defs>
      {/* DRP text */}
      <text
        x="20"
        y="40"
        fill={textColor}
        fontFamily="'Syne',sans-serif"
        fontSize="38"
        fontWeight="800"
        letterSpacing="2">
        DRP
      </text>
      {/* X with glow + color pulse */}
      <text
        x="176"
        y="44"
        fontFamily="'Syne',sans-serif"
        fontSize="54"
        fontWeight="800"
        fill="#f59e0b"
        filter="url(#xGlow)">
        <animate
          attributeName="fill"
          values="#f59e0b;#fbbf24;#f59e0b"
          dur="4s"
          repeatCount="indefinite" />
        X
      </text>
      {/* Pin with bounce */}
      <g>
        <animateTransform
          attributeName="transform"
          type="translate"
          values="0 0;0 -3;0 0"
          dur="3s"
          repeatCount="indefinite" />
        <path
          d="M198 -10 Q212 -10 212 4 Q212 14 198 24 Q184 14 184 4 Q184 -10 198 -10 Z"
          fill="#4ade80">
          <animate
            attributeName="opacity"
            values=".85;1;.85"
            dur="3s"
            repeatCount="indefinite" />
        </path>
        <circle cx="198" cy="4" r="5" fill="#fff" />
      </g>
      {/* Arrow base line (dim) */}
      <line
        x1="22"
        y1="54"
        x2="222"
        y2="54"
        stroke="#16a34a"
        strokeWidth="2"
        strokeLinecap="round"
        opacity=".35" />
      {/* Arrow traveling highlight */}
      <line
        x1="22"
        y1="54"
        x2="222"
        y2="54"
        stroke="#4ade80"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="40 160"
        clipPath="url(#arrowClip)">
        <animate
          attributeName="stroke-dashoffset"
          from="200"
          to="0"
          dur="2s"
          repeatCount="indefinite" />
      </line>
      {/* Arrow head with bob */}
      <g>
        <animateTransform
          attributeName="transform"
          type="translate"
          values="0 0;3 0;0 0"
          dur="2s"
          repeatCount="indefinite" />
        <path
          d="M218 48 L228 54 L218 60"
          stroke="#4ade80"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none" />
      </g>
      {/* Courier text */}
      <text
        x="242"
        y="40"
        fill={isDark ? 'rgba(255,255,255,.5)' : 'rgba(12,26,14,.4)'}
        fontFamily="'Syne',sans-serif"
        fontSize="22"
        fontWeight="700"
        letterSpacing="1">
        Courier
      </text>
    </svg>
  );
}

interface DrpxLogoIconProps {
  /** Skalierungsfaktor (Default: `1` = 68×68 px). */
  size?: number;
}

/** DRPX Courier — kompakte Icon-Variante (quadratisches App-Icon). */
export function DrpxLogoIcon({ size }: DrpxLogoIconProps) {
  const s = size || 1;
  return (
    <svg
      viewBox="0 0 68 68"
      style={{ width: 68 * s, height: 68 * s, display: 'block' }}>
      <rect x="0" y="0" width="68" height="68" rx="16" fill="#16a34a" />
      <text
        x="4"
        y="42"
        fill="#fff"
        fontFamily="'Syne',sans-serif"
        fontSize="24"
        fontWeight="800">
        D
      </text>
      <text
        x="28"
        y="44"
        fill="#f59e0b"
        fontFamily="'Syne',sans-serif"
        fontSize="28"
        fontWeight="800">
        X
      </text>
      <path
        d="M48 8 Q56 8 56 16 Q56 22 48 28 Q40 22 40 16 Q40 8 48 8 Z"
        fill="#4ade80" />
      <circle cx="48" cy="16" r="3.5" fill="#fff" />
      <line
        x1="8"
        y1="54"
        x2="52"
        y2="54"
        stroke="#4ade80"
        strokeWidth="1.5"
        strokeLinecap="round" />
      <path
        d="M49 50 L55 54 L49 58"
        stroke="#4ade80"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none" />
    </svg>
  );
}
